import { GatsbyImage, getImage, ImageDataLike } from 'gatsby-plugin-image';
import React from 'react';
import { CardSection } from './CardSection';
import { DisclosureSection } from './DisclosureSection';
import { ParagraphSection } from './ParagraphSection';

type BlockType = 'card' | 'paragraph' | 'disclosure';

export interface Site {
  landingPage: Page;
  navPages: Page[];
}

export interface Page {
  id: string;
  title: string;
  slug: string;
  url: string;
  blocks: Array<Block>;
  subpages: Array<Page>;
  pageLineage: Array<Page>;
  cousinPages: Array<Page>;
}

export interface Block {
  id: string;
  slug: string;
  heading?: string;
  subheading?: string;
  blockType: BlockType;
  galleryImages?: Array<{ thumb: unknown; full: unknown }>;
  link: boolean;
  url: string;
  body?: { childMarkdownRemark: { html: string; excerpt: string } };
  heroImage?: ImageDataLike;
  preserveHeroAspectRatio: boolean;
  cousinPages: Array<Page>;
  pageLineage: Array<Page>;
}

export interface Section {
  id: string;
  type: BlockType;
  blocks: Array<Block>;
}

export const BlockHero = ({
  block,
  className = '',
}: {
  block: Block;
  className?: string;
}) => {
  if (!block.heroImage) {
    return null;
  }
  return (
    <GatsbyImage
      image={getImage(block.heroImage)}
      alt={block.heading}
      imgStyle={{
        objectFit: block.preserveHeroAspectRatio ? 'contain' : undefined,
      }}
      // imgClassName="object-contain" <- this doesn't work feb 2022
      className={className}
      // ^ beware, passing in "aspect-x" classnames may not override the existing "aspect-video"
      // because of the order in which they're defined by tailwind...
    />
  );
};

export const ContentBlocks = ({
  blocks,
  className = '',
}: {
  blocks: Array<Block>;
  className?: string;
}) => {
  const transformed = blocks.reduce((sections: Array<Section>, block) => {
    const lastSection =
      sections.length > 0 ? sections[sections.length - 1] : null;
    const blockType: BlockType = block.link ? 'card' : block.blockType;
    if (lastSection?.type === blockType) {
      lastSection.blocks = [...lastSection.blocks, block];
    } else {
      sections = [
        ...sections,
        {
          type: blockType,
          blocks: [block],
          id: `${blockType}_${block.id}`,
        },
      ];
    }
    return sections;
  }, [] as Array<Section>);
  return (
    <div className={className}>
      {transformed.map((s) => (
        <div key={s.id}>
          {s.type == 'paragraph' && <ParagraphSection section={s} />}
          {s.type == 'card' && <CardSection section={s} />}
          {s.type == 'disclosure' && <DisclosureSection section={s} />}
        </div>
      ))}
    </div>
  );
};
